import Loading, { LoadingLinear } from "@ingka/loading";

import React from "react";
import { StyledToolbarButton } from "../../../shared/styles/components/integrations.styles";
import { getStageMessage } from "../utils/excel-pipeline.utils";
import { rgba } from "../../../shared/styles/core/utils";
import styled from "@emotion/styled";
import { theme } from "../../../shared/styles/core/theme";
import { tokens } from "../../../shared/styles/core/tokens";

interface ExportProgressDialogProps {
   isOpen: boolean;
   progress: number;
   processedItems: number;
   totalItems: number;
   processedRows: number;
   totalRows: number;
   stage: 'fetching' | 'processing' | 'generating' | 'complete' | 'cancelling';
   currentFile?: number;
   totalFiles?: number;
   onCancel?: () => void;
}

const Overlay = styled.div<{ isOpen: boolean }>`
   display: ${props => props.isOpen ? 'flex' : 'none'};
   position: fixed;
   top: 0;
   left: 0;
   right: 0;
   bottom: 0;
   background-color: ${rgba(theme.colors.text.primary, 0.5)};
   z-index: ${tokens.zIndex.modal};
   align-items: center;
   justify-content: center;
`;

const DialogCard = styled.div`
   background: ${theme.colors.bg.primary};
   border-radius: ${tokens.radius.lg};
   padding: ${tokens.space.xl};
   min-width: 400px;
   max-width: 500px;
   box-shadow: ${tokens.shadow.xl};
`;

const DialogTitle = styled.h2`
   margin: 0 0 ${tokens.space.md} 0;
   font-size: ${tokens.font.size.xl};
   font-weight: ${tokens.font.weight.bold};
   color: ${theme.colors.text.primary};
`;

const ProgressContainer = styled.div`
   margin: ${tokens.space.lg} 0;
`;

const ProgressText = styled.div`
   margin-bottom: ${tokens.space.sm};
   font-size: ${tokens.font.size.md};
   color: ${theme.colors.text.tertiary};
   display: flex;
   justify-content: space-between;
   align-items: center;
`;

const StageText = styled.span`
   font-weight: ${tokens.font.weight.medium};
`;

const ProgressPercentage = styled.span`
   font-weight: ${tokens.font.weight.bold};
   color: ${theme.colors.text.primary};
`;

const Counter = styled.div`
   margin-top: ${tokens.space.xs};
   font-size: ${tokens.font.size.sm};
   color: ${theme.colors.text.tertiary};
   text-align: center;
`;

const ButtonContainer = styled.div`
   margin-top: ${tokens.space.lg};
   display: flex;
   justify-content: center;
`;

export const ExportProgressDialog: React.FC<ExportProgressDialogProps> = ({
   isOpen,
   progress,
   processedItems,
   totalItems,
   processedRows,
   totalRows,
   stage,
   currentFile,
   totalFiles,
   onCancel,
}) => {
   const showItemCount = totalItems > 0 && stage !== 'cancelling';
   const showRowCount = totalRows > 0 && stage !== 'fetching' && stage !== 'cancelling';
   const showCancelButton = stage !== 'complete' && stage !== 'cancelling' && onCancel;

   return (
      <Overlay isOpen={isOpen}>
         <DialogCard>
            <DialogTitle>Exporting to Excel</DialogTitle>

            <ProgressContainer>
               <ProgressText>
                  <StageText>{getStageMessage(stage, currentFile, totalFiles)}</StageText>
                  {stage !== 'cancelling' && <ProgressPercentage>{progress}%</ProgressPercentage>}
               </ProgressText>

               <Loading text="">
                  <LoadingLinear loadingValue={progress} />
               </Loading>

               {showItemCount && (
                  <Counter>
                     {stage === 'fetching'
                        ? `${totalItems.toLocaleString()} items fetched`
                        : `${processedItems.toLocaleString()} of ${totalItems.toLocaleString()} items`
                     }
                  </Counter>
               )}

               {showRowCount && (
                  <Counter>
                     {processedRows.toLocaleString()} of {totalRows.toLocaleString()} rows
                  </Counter>
               )}
            </ProgressContainer>

            {showCancelButton && (
               <ButtonContainer>
                  <StyledToolbarButton
                     onClick={onCancel}
                     text="Cancel Export"
                     small={true}
                  />
               </ButtonContainer>
            )}
         </DialogCard>
      </Overlay>
   );
};